<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      ref="modalRoot"
      class="package-builder-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="headingId"
    >
      <div
        class="package-builder-modal__backdrop"
        @click="handleBackdropClick"
      />
      <div class="package-builder-modal__panel">
        <button
          type="button"
          class="btn btn-link package-builder-modal__close"
          :disabled="isSubmitting"
          @click="handleClose"
          :aria-label="closeAriaLabel"
        >
          <FontAwesomeIcon :icon="appIcons.faXmark" />
        </button>
        <div
          v-if="editCartBundleId"
          class="package-builder-modal__body"
        >
          <PackageBuilderHeader
            :heading-id="headingId"
            :cart-bundle-id="activeCartBundleId"
          />
          <PackageBuilderCountdown :cart-bundle-id="activeCartBundleId" />
          <SheetSelectionGrid :cart-bundle-id="activeCartBundleId" />
        </div>
        <PackageBuilderFooter />
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'

import { useAppContentStore } from '@/stores/appContent'
import { usePackageBuilderStore } from '@/stores/packageBuilder'
import { mapActions, mapState, mapStores } from 'pinia'

import { onKeyStroke } from '@vueuse/core'

import useAppIcons from '@/composables/appIcons'
import { useFocusTrap } from '@/composables/useFocusTrap'

import {
  type BuilderContent,
  resolveBuilderContent
} from '@/components/Senior/PackageBuilder/content'
import PackageBuilderCountdown from '@/components/Senior/PackageBuilder/PackageBuilderCountdown.vue'
import PackageBuilderFooter from '@/components/Senior/PackageBuilder/PackageBuilderFooter.vue'
import PackageBuilderHeader from '@/components/Senior/PackageBuilder/PackageBuilderHeader.vue'
import SheetSelectionGrid from '@/components/Senior/PackageBuilder/SheetSelectionGrid.vue'

export default defineComponent({
  name: 'PackageBuilderModal',
  components: {
    PackageBuilderCountdown,
    PackageBuilderFooter,
    PackageBuilderHeader,
    SheetSelectionGrid
  },
  setup() {
    const store = usePackageBuilderStore()
    onKeyStroke('Escape', () => {
      if (store.isOpen && !store.isSubmitting) store.closeModal()
    })
    const modalRoot = ref<HTMLElement | null>(null)
    useFocusTrap(modalRoot, () => store.isOpen)
    return { ...useAppIcons(), modalRoot }
  },
  computed: {
    ...mapStores(useAppContentStore),
    ...mapState(usePackageBuilderStore, ['isOpen', 'isSubmitting', 'editCartBundleId']),
    content(): BuilderContent {
      return resolveBuilderContent(this.appContentStore.seniorPackageBuilderContent)
    },
    // Non-null id for the child props; the body only renders when
    // `editCartBundleId` is set (see template `v-if`).
    activeCartBundleId(): string {
      return this.editCartBundleId ?? ''
    },
    headingId(): string {
      return `package-builder-modal-heading-${this.$.uid}`
    },
    closeAriaLabel(): string {
      return this.content.closeAriaLabel
    }
  },
  methods: {
    ...mapActions(usePackageBuilderStore, { closeStore: 'closeModal' }),
    handleClose() {
      if (this.isSubmitting) return
      this.closeStore()
    },
    handleBackdropClick() {
      if (this.isSubmitting) return
      this.closeStore()
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(open: boolean) {
        if (typeof document !== 'undefined') {
          document.body.style.overflow = open ? 'hidden' : ''
        }
      }
    }
  },
  beforeUnmount() {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = ''
    }
  }
})
</script>

<style scoped lang="scss">
.package-builder-modal {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  flex-direction: column;

  &__backdrop {
    display: none;
  }

  &__panel {
    position: relative;
    background: #fff;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__close {
    position: absolute;
    top: 2rem;
    right: 1.75rem;
    color: #000;
    font-size: 1.25rem;
    padding: 0;
    z-index: 2;
    line-height: 1;
  }

  &__body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 1rem;
    padding-top: 2.75rem;
    padding-bottom: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  @include media-breakpoint-up(md) {
    align-items: center;
    justify-content: center;

    .package-builder-modal__backdrop {
      display: block;
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
    }

    .package-builder-modal__panel {
      position: relative;
      width: calc(100vw - 32px);
      max-width: 960px;
      max-height: calc(100vh - 64px);
      height: auto;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    }

    .package-builder-modal__body {
      padding: 2rem 2.5rem;
      padding-top: 2rem;
    }
  }
}
</style>
